class RefillQuestionnaireModal {
  constructor() {
    // Load questionnaire data from the snippet
    this.loadQuestionnaireData();
    
    this.currentQuestion = 0;
    this.answers = {};
    this.totalPoints = 0;
    this.modal = null;
    this.init();
  }

  loadQuestionnaireData() {
    try {
      const dataElement = document.getElementById('questionnaire-data');
      if (!dataElement) {
        console.error('Questionnaire data not found');
        return;
      }

      const data = JSON.parse(dataElement.textContent);
      this.questions = data.questions;
      this.recommendations = data.recommendations;
      this.translations = data.translations;
    } catch (error) {
      console.error('Error loading questionnaire data:', error);
      // Fallback to default data if loading fails
      this.questions = [];
      this.recommendations = {};
      this.translations = {};
    }
  }

  init() {
    this.modal = document.querySelector('.refill-questionnaire-modal');
    if (!this.modal) return;

    this.bindEvents();
    this.showQuestion(0);
  }

  bindEvents() {
    if (!this.modal) return;

    // Modal trigger
    const trigger = document.querySelector('.questionnaire-trigger');
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      });
    }

    // Modal close button
    const closeBtn = this.modal.querySelector('.questionnaire-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }

    // Close modal when clicking outside
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.classList.contains('hidden')) {
        this.closeModal();
      }
    });

    // Navigation buttons
    const prevBtn = this.modal.querySelector('.prev-btn');
    const nextBtn = this.modal.querySelector('.next-btn');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.previousQuestion();
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextQuestion();
      });
    }

    // Apply recommendation button
    const applyBtn = this.modal.querySelector('.apply-recommendation');
    if (applyBtn) {
      applyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.applyRecommendation();
      });
    }

    // Restart questionnaire button
    const restartBtn = this.modal.querySelector('.restart-questionnaire');
    if (restartBtn) {
      restartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.restartQuestionnaire();
      });
    }
  }

  openModal() {
    if (this.modal) {
      this.modal.classList.remove('hidden');
      this.modal.classList.add('flex');
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.classList.add('hidden');
      this.modal.classList.remove('flex');
      document.body.style.overflow = ''; // Restore scrolling
    }
  }

  showQuestion(index) {
    const question = this.questions[index];
    const questionContainer = this.modal.querySelector('.question-container');
    const progressFill = this.modal.querySelector('.progress-fill');
    const currentQuestionSpan = this.modal.querySelector('.current-question');
    const prevBtn = this.modal.querySelector('.prev-btn');
    const nextBtn = this.modal.querySelector('.next-btn');

    if (!question || !questionContainer) return;

    // Update progress
    const progress = ((index + 1) / this.questions.length) * 100;
    if (progressFill) progressFill.style.width = `${progress}%`;
    if (currentQuestionSpan) currentQuestionSpan.textContent = index + 1;

    // Update navigation buttons
    if (prevBtn) {
      prevBtn.disabled = index === 0;
      prevBtn.textContent = this.translations.previous || 'Previous';
    }
    if (nextBtn) nextBtn.textContent = index === this.questions.length - 1 ? (this.translations.getRecommendation || 'Get Recommendation') : (this.translations.next || 'Next');

    // Create question HTML
    const questionHTML = `
      <div class="question" data-question-id="${question.id}">
        <h4 class="mb-4 text-base font-medium">${question.question}</h4>
        <div class="options space-y-3">
          ${question.options.map((option, optionIndex) => `
            <label class="option-label flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50">
              <input type="radio" name="${question.id}" value="${option.points}" class="sr-only">
              <div class="radio-circle flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-300">
                <div class="radio-dot bg-yellow h-2 w-2 rounded-full opacity-0 transition-opacity"></div>
              </div>
              <span class="option-text">${option.text}</span>
            </label>
          `).join('')}
        </div>
      </div>
    `;

    questionContainer.innerHTML = questionHTML;

    // Bind option selection events
    const optionLabels = questionContainer.querySelectorAll('.option-label');
    optionLabels.forEach((label, optionIndex) => {
      const radio = label.querySelector('input[type="radio"]');
      const radioCircle = label.querySelector('.radio-circle');
      const radioDot = label.querySelector('.radio-dot');

      label.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Uncheck all other options
        optionLabels.forEach(l => {
          l.classList.remove('selected');
          l.querySelector('.radio-circle').classList.remove('border-yellow');
          l.querySelector('.radio-dot').classList.add('opacity-0');
        });

        // Check this option
        label.classList.add('selected');
        radioCircle.classList.add('border-yellow');
        radioDot.classList.remove('opacity-0');
        radio.checked = true;

        // Store the answer
        this.answers[question.id] = parseInt(radio.value);
      });
    });

    // Pre-select if answer exists
    if (this.answers[question.id] !== undefined) {
      const existingAnswer = this.answers[question.id];
      const matchingOption = question.options.findIndex(option => option.points === existingAnswer);
      if (matchingOption >= 0) {
        const optionLabel = optionLabels[matchingOption];
        optionLabel.click();
      }
    }
  }

  nextQuestion() {
    const currentQuestion = this.questions[this.currentQuestion];
    const selectedAnswer = this.answers[currentQuestion.id];

    if (selectedAnswer === undefined) {
      // Show error or highlight that an option needs to be selected
      return;
    }

    if (this.currentQuestion < this.questions.length - 1) {
      this.currentQuestion++;
      this.showQuestion(this.currentQuestion);
    } else {
      this.calculateRecommendation();
    }
  }

  previousQuestion() {
    if (this.currentQuestion > 0) {
      this.currentQuestion--;
      this.showQuestion(this.currentQuestion);
    }
  }

  calculateRecommendation() {
    // Calculate total points
    this.totalPoints = Object.values(this.answers).reduce((sum, points) => sum + points, 0);

    // Find the appropriate recommendation
    let recommendation = null;
    for (const [key, plan] of Object.entries(this.recommendations)) {
      if (this.totalPoints >= plan.range[0] && this.totalPoints <= plan.range[1]) {
        recommendation = { ...plan, key };
        break;
      }
    }

    this.showRecommendation(recommendation);
  }

  showRecommendation(recommendation) {
    const questionContainer = this.modal.querySelector('.question-container');
    const navigationContainer = this.modal.querySelector('.questionnaire-navigation');
    const recommendationContainer = this.modal.querySelector('.recommendation-container');

    if (!recommendation || !questionContainer || !navigationContainer || !recommendationContainer) return;

    // Hide question and navigation
    questionContainer.style.display = 'none';
    navigationContainer.style.display = 'none';

    // Show recommendation
    recommendationContainer.classList.remove('hidden');

    // Populate recommendation data
    const planName = recommendationContainer.querySelector('.plan-name');
    const planFrequency = recommendationContainer.querySelector('.plan-frequency');
    const planDescription = recommendationContainer.querySelector('.plan-description');
    const planNote = recommendationContainer.querySelector('.plan-note');

    if (planName) planName.textContent = recommendation.name;
    if (planFrequency) planFrequency.textContent = `${this.translations.delivery || 'Delivery'} ${recommendation.frequency}`;
    if (planDescription) planDescription.textContent = recommendation.description;
    if (planNote) planNote.textContent = recommendation.handBodyNote;

    // Store recommendation for later use
    this.currentRecommendation = recommendation;
  }

  applyRecommendation() {
    if (!this.currentRecommendation) return;

    // Find the selling plan select and update it based on the recommendation
    const sellingPlanSelect = document.querySelector('select[name="selling_plan"]');
    if (sellingPlanSelect) {
      // Map recommendation to selling plan options
      const frequencyMap = {
        'Every 6 weeks': '691699614042',
        'Every 8 weeks': '691592036698', 
        'Every 10 weeks': '691699679578',
        'Every 12 weeks': '691699745114'
      };

      const targetValue = frequencyMap[this.currentRecommendation.frequency];
      
      // Find and select the appropriate selling plan
      if (targetValue) {
        sellingPlanSelect.value = targetValue;
        sellingPlanSelect.dispatchEvent(new Event('change'));
      } else {
        // Fallback: try to match by text content
        for (let i = 0; i < sellingPlanSelect.options.length; i++) {
          const option = sellingPlanSelect.options[i];
          if (option.text.toLowerCase().includes(this.currentRecommendation.frequency.toLowerCase())) {
            sellingPlanSelect.value = option.value;
            sellingPlanSelect.dispatchEvent(new Event('change'));
            break;
          }
        }
      }
    }

    // Add the product to cart with the selected recommendation
    this.addProductToCart();

    // Close the modal
    this.closeModal();
  }

  async addProductToCart() {
    try {
      // Find the main product form
      const productForm = document.querySelector('form[action="/cart/add"]');
      if (!productForm) {
        console.error('Product form not found');
        return;
      }

      // Get the variant ID from the form
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (!variantIdInput) {
        console.error('Variant ID input not found');
        return;
      }

      const variantId = parseInt(variantIdInput.value, 10);
      if (isNaN(variantId) || variantId <= 0) {
        console.error('Invalid variant ID');
        return;
      }

      // Get quantity (default to 1)
      const quantitySelect = productForm.querySelector('select[name="quantity"]');
      const quantity = quantitySelect ? parseInt(quantitySelect.value, 10) || 1 : 1;

      // Prepare cart data
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      // Add selling plan if selected
      const sellingPlanSelect = document.querySelector('select[name="selling_plan"]');
      if (sellingPlanSelect && sellingPlanSelect.value && sellingPlanSelect.value !== 'one-time') {
        formData.items[0].selling_plan = parseInt(sellingPlanSelect.value, 10);
      }

      // Use the cart handler to add to cart and show notification
      if (window.cartHandler) {
        await window.cartHandler.addToCart(formData);
      } else {
        // Fallback: direct API call if cart handler is not available
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error('Failed to add item to cart');
        }

        // Show a simple success message if cart handler is not available
        this.showSuccessMessage();
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      // Show error message to user
      this.showErrorMessage('Failed to add product to cart. Please try again.');
    }
  }

  showErrorMessage(message) {
    // Remove any existing error notifications first
    const existingNotifications = document.querySelectorAll('.recommendation-error');
    existingNotifications.forEach(notification => {
      notification.remove();
    });

    const errorHTML = `
      <div class="recommendation-error border-red bg-red/50 mb-6 rounded-lg border p-4">
        <div class="flex items-center gap-3">
          <div class="bg-red flex h-6 w-6 items-center justify-center rounded-full">
            <svg class="text-red-dark h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <div>
            <p class="text-red-dark text-sm font-medium">Error</p>
            <p class="text-red-dark text-xs">${message}</p>
          </div>
        </div>
      </div>
    `;

    const sellingPlansWrapper = document.querySelector('.selling-plans-wrapper');
    if (sellingPlansWrapper) {
      sellingPlansWrapper.insertAdjacentHTML('beforebegin', errorHTML);
    }
  }



  restartQuestionnaire() {
    this.currentQuestion = 0;
    this.answers = {};
    this.totalPoints = 0;
    this.currentRecommendation = null;

    const questionContainer = this.modal.querySelector('.question-container');
    const navigationContainer = this.modal.querySelector('.questionnaire-navigation');
    const recommendationContainer = this.modal.querySelector('.recommendation-container');

    if (questionContainer && navigationContainer && recommendationContainer) {
      questionContainer.style.display = 'block';
      navigationContainer.style.display = 'flex';
      recommendationContainer.classList.add('hidden');
      this.showQuestion(0);
    }
  }
}

// Initialize questionnaire modal when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize on any page that has the questionnaire modal element
  const questionnaireModalElement = document.querySelector('.refill-questionnaire-modal');
  const questionnaireTrigger = document.querySelector('.questionnaire-trigger');
  
  // Only initialize if both the modal and trigger exist (product pages with selling plans)
  if (questionnaireModalElement && questionnaireTrigger) {
    new RefillQuestionnaireModal();
  }
}); 